<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <Overlayscreen :visible="showReload">
    <div class="flex h-screen items-center justify-center">
      <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          class="relative my-8 w-96 max-w-lg overflow-hidden rounded-lg bg-white p-4 text-left shadow-xl transition-all"
        >
          <div class="bg-white px-4 py-3">
            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:text-left">
                <h3 id="modal-title" class="text-base font-semibold leading-6 text-gray-900">
                  {{ ERROR_MESSAGES[state]?.title }}
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    {{ ERROR_MESSAGES[state]?.message }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="!['initializing', 'loading'].includes(state) || showButtonAfterDelay"
            class="flex flex-row-reverse justify-between px-4 py-3"
          >
            <router-link to="/logout" class="button-outlined-blue">
              {{ $t('ui.common.relogin') }}
            </router-link>
            <button type="button" class="button-outlined-white" @click="reloadPage">
              {{ $t('ui.common.reload') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Overlayscreen>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useSharedWorker } from '@/composables/useSharedworker'
import Overlayscreen from '@/components/OverlayScreen.vue'

const LOADING_TIMEOUT = 10_000

const worker = useSharedWorker()

const workerIsStall = ref(false)
const workerIsTerminated = ref(false)
const showReload = computed(() => workerIsStall.value || workerIsTerminated.value)
const showButtonAfterDelay = ref(false)

const wait = (s: number) => new Promise((resolve) => setTimeout(resolve, s))

import { useTranslation } from 'i18next-vue'
const { t } = useTranslation()

const ERROR_MESSAGES = computed<Record<string, Record<string, string>>>(() => ({
  initializing: {
    title: t('ui.workerStatus.initializingTitle'),
    message: t('ui.workerStatus.initializingMessage')
  },
  loading: {
    title: t('ui.workerStatus.initializingTitle'),
    message: t('ui.workerStatus.initializingMessage')
  },
  closed: {
    title: t('ui.workerStatus.systemTerminatedTitle'),
    message: t('ui.workerStatus.systemErrorMessage')
  },
  error: {
    title: t('ui.workerStatus.systemErrorTitle'),
    message: t('ui.workerStatus.systemErrorMessage')
  },
  unknown: {
    title: t('ui.workerStatus.unknownTitle'),
    message: t('ui.workerStatus.unknownMessage')
  }
}))
const state = ref('unknown')

// 10秒以上準備に時間がかかったら、リロードボタンを表示する
watch(state, (newState) => {
  if (['initializing', 'loading'].includes(newState)) {
    setTimeout(() => {
      if (['initializing', 'loading'].includes(state.value)) {
        showButtonAfterDelay.value = true
      }
    }, LOADING_TIMEOUT)
  } else {
    showButtonAfterDelay.value = false
  }
})

onMounted(async () => {
  // Shared worker の state により、showReload を トグルする
  for (;;) {
    state.value = await worker.waitState('closed', 'error', 'initializing', 'loading')
    workerIsStall.value = true
    state.value = await worker.waitState('SyncRunning')
    workerIsStall.value = false
  }
})

onMounted(async () => {
  // Shared worker が terminate された場合に、 showReload を true にする。
  // terminate のイベントハンドラは無いっぽいので、ポーリングしている。
  // workerTimeout 待っても、Shared worker が返ってこないときは死んでいるとみなす
  // また、ワーカーが一度Terminate すると、通信ソケットが変わってしまう。
  // ページリロードをしたほうが安全なので、自動復帰はさせない。
  const workerTimeout = 5000
  for (;;) {
    const ret = await Promise.race([worker.waitState(), wait(workerTimeout)])
    workerIsTerminated.value = ret === undefined
    await wait(1000)
  }
})
const reloadPage = () => window.location.reload()
</script>
